/**
 * GMAssistantPage
 *
 * Standalone popup window for GM reference content.
 * Syncs session plan state from the main window via BroadcastChannel.
 * No dice relay needed — this is a read-only reference panel.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSessionStore } from '../stores/sessionStore';
import { useCampaignStore } from '../stores/campaignStore';
import { initSessionPlanSync, requestSessionPlanSync, destroySessionPlanSync } from '../stores/sessionPlanStore';
import { Icon } from './Icon';
import { GMAssistantLayout } from './gm-assistant/GMAssistantLayout';

interface GMAssistantPageProps {
  campaignId: string;
}

export function GMAssistantPage({ campaignId }: GMAssistantPageProps) {
  const { user } = useAuthStore();
  const initializeUser = useSessionStore((s) => s.initializeUser);
  const userId = useSessionStore((s) => s.userId);
  const isInitialized = useSessionStore((s) => s.isInitialized);
  const setCurrentCampaign = useCampaignStore((s) => s.setCurrentCampaign);

  const hasInitRef = useRef(false);

  // Step 1: Initialize session
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeUser();
  }, [initializeUser]);

  // Step 2: Set campaign once auth is ready
  useEffect(() => {
    if (!userId || !isInitialized) return;
    setCurrentCampaign(campaignId);
  }, [userId, isInitialized, campaignId, setCurrentCampaign]);

  // Step 3: Init BroadcastChannel sync + request state from main window
  useEffect(() => {
    initSessionPlanSync();
    requestSessionPlanSync();
    return () => {
      destroySessionPlanSync();
    };
  }, []);

  // Set window title
  useEffect(() => {
    document.title = 'GM Assistant \u2014 Machina';
  }, []);

  if (!user) {
    return (
      <div style={styles.loadingContainer}>
        <Icon name="lock" size={32} color="rgba(192, 192, 192, 0.4)" />
        <div style={styles.loadingText}>Not signed in</div>
        <div style={styles.loadingSubtext}>Open Machina in another tab and sign in first.</div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div style={styles.loadingContainer}>
        <Icon name="loader" size={32} color="#d4af37" />
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <GMAssistantLayout campaignId={campaignId} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#0a080c',
    color: '#e0dcd0',
    overflow: 'hidden',
    fontFamily: 'Georgia, serif',
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: '#0a080c',
  },
  loadingText: {
    fontSize: 14,
    color: 'rgba(192, 192, 192, 0.7)',
    fontFamily: 'Georgia, serif',
  },
  loadingSubtext: {
    fontSize: 12,
    color: 'rgba(192, 192, 192, 0.4)',
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
  },
};
